import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Star, Plus, X, Check, Edit2, Trash2, Search, ArrowLeft, Home } from "lucide-react";

interface Instructor {
  id: string;
  name?: string;
  displayName?: string;
  email: string;
  profileImage?: string;
  bio?: string;
  coursesCount?: number;
  studentsCount?: number;
}

interface FeaturedPartner {
  id: string;
  instructorId: string;
  instructorName: string;
  instructorImage: string;
  description: string;
  coursesCount: number;
  studentsCount: number;
  order: number;
  isActive: boolean;
}

export default function FeaturedPartners() {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [partners, setPartners] = useState<FeaturedPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [description, setDescription] = useState("");

  // Load instructors and partners
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all instructors (including admins)
      const usersSnapshot = await getDocs(collection(db, "users"));
      const instructorsData = usersSnapshot.docs
        .filter(doc => {
          const role = doc.data().role;
          return role === "instructor" || role === "admin";
        })
        .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Instructor));

      // Load courses count for each instructor
      const instructorsWithCounts = await Promise.all(
        instructorsData.map(async (instructor) => {
          const coursesQuery = query(
            collection(db, "courses"),
            where("instructorId", "==", instructor.id)
          );
          const coursesSnapshot = await getDocs(coursesQuery);
          
          let studentsCount = 0;
          for (const courseDoc of coursesSnapshot.docs) {
            const enrollmentsQuery = query(
              collection(db, "enrollments"),
              where("courseId", "==", courseDoc.id)
            );
            const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
            studentsCount += enrollmentsSnapshot.size;
          }

          return {
            ...instructor,
            coursesCount: coursesSnapshot.size,
            studentsCount
          };
        })
      );

      setInstructors(instructorsWithCounts);

      // Load featured partners
      const partnersSnapshot = await getDocs(collection(db, "featuredPartners"));
      const partnersData = partnersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FeaturedPartner));
      partnersData.sort((a, b) => a.order - b.order);
      setPartners(partnersData);
    } catch (error) {
      console.error("Error loading data:", error);
      alert("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPartner = async () => {
    if (!selectedInstructor) return;

    try {
      const instructorName = selectedInstructor.displayName || selectedInstructor.name || "ไม่ระบุชื่อ";
      
      const newPartner = {
        instructorId: selectedInstructor.id,
        instructorName: instructorName,
        instructorImage: selectedInstructor.profileImage || "",
        description: description || selectedInstructor.bio || "",
        coursesCount: selectedInstructor.coursesCount || 0,
        studentsCount: selectedInstructor.studentsCount || 0,
        order: partners.length,
        isActive: true
      };

      await addDoc(collection(db, "featuredPartners"), newPartner);
      alert("เพิ่มพาร์ทเนอร์สำเร็จ!");
      setShowAddModal(false);
      setSelectedInstructor(null);
      setDescription("");
      loadData();
    } catch (error) {
      console.error("Error adding partner:", error);
      alert("เกิดข้อผิดพลาดในการเพิ่มพาร์ทเนอร์");
    }
  };

  const handleToggleActive = async (partnerId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, "featuredPartners", partnerId), {
        isActive: !currentStatus
      });
      loadData();
    } catch (error) {
      console.error("Error toggling partner:", error);
      alert("เกิดข้อผิดพลาด");
    }
  };

  const handleDeletePartner = async (partnerId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ที่จะลบพาร์ทเนอร์นี้?")) return;

    try {
      await deleteDoc(doc(db, "featuredPartners", partnerId));
      alert("ลบพาร์ทเนอร์สำเร็จ!");
      loadData();
    } catch (error) {
      console.error("Error deleting partner:", error);
      alert("เกิดข้อผิดพลาดในการลบพาร์ทเนอร์");
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    
    try {
      const partner1 = partners[index];
      const partner2 = partners[index - 1];
      
      await updateDoc(doc(db, "featuredPartners", partner1.id), { order: index - 1 });
      await updateDoc(doc(db, "featuredPartners", partner2.id), { order: index });
      
      loadData();
    } catch (error) {
      console.error("Error moving partner:", error);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === partners.length - 1) return;
    
    try {
      const partner1 = partners[index];
      const partner2 = partners[index + 1];
      
      await updateDoc(doc(db, "featuredPartners", partner1.id), { order: index + 1 });
      await updateDoc(doc(db, "featuredPartners", partner2.id), { order: index });
      
      loadData();
    } catch (error) {
      console.error("Error moving partner:", error);
    }
  };

  const filteredInstructors = instructors.filter(instructor => {
    const isAlreadyPartner = partners.some(p => p.instructorId === instructor.id);
    const instructorName = instructor.displayName || instructor.name || "";
    const matchesSearch = instructorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         instructor.email.toLowerCase().includes(searchTerm.toLowerCase());
    return !isAlreadyPartner && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-textDark">
                  จัดการพาร์ทเนอร์หน้าแรก
                </h1>
                <p className="text-textLight text-sm">
                  เลือกผู้สอนที่จะแสดงในส่วนพาร์ทเนอร์หน้าแรก
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                เพิ่มพาร์ทเนอร์
              </button>
              <Link to="/">
                <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                  <Home className="w-5 h-5" />
                  <span>ไปหน้าแรก</span>
                </button>
              </Link>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>คำแนะนำ:</strong> พาร์ทเนอร์จะแสดงในหน้าแรกแบบสไลด์อัตโนมัติ 
              คุณสามารถเพิ่มได้ไม่จำกัด แต่แนะนำให้ใส่ 6-9 คนเพื่อความสวยงาม
            </p>
          </div>
        </div>

        {/* Current Partners */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-textDark mb-4">
            พาร์ทเนอร์ปัจจุบัน ({partners.length} คน)
          </h2>

          {partners.length === 0 ? (
            <div className="text-center py-12 text-textLight">
              <Star className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>ยังไม่มีพาร์ทเนอร์</p>
              <p className="text-sm">คลิก "เพิ่มพาร์ทเนอร์" เพื่อเริ่มต้น</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map((partner, index) => (
                <div
                  key={partner.id}
                  className={`border-2 rounded-xl p-4 ${
                    partner.isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {partner.instructorImage ? (
                      <img
                        src={partner.instructorImage}
                        alt={partner.instructorName}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center text-2xl font-bold text-white">
                        {partner.instructorName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-textDark">{partner.instructorName}</h3>
                      <p className="text-xs text-textLight line-clamp-2">{partner.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                    <span>📚 {partner.coursesCount} คอร์ส</span>
                    <span>👥 {partner.studentsCount} คน</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="p-2 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                      title="เลื่อนขึ้น"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === partners.length - 1}
                      className="p-2 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                      title="เลื่อนลง"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => handleToggleActive(partner.id, partner.isActive)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold ${
                        partner.isActive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {partner.isActive ? '✓ แสดง' : '✕ ซ่อน'}
                    </button>
                    <button
                      onClick={() => handleDeletePartner(partner.id)}
                      className="p-2 hover:bg-red-100 text-red-600 rounded-lg"
                      title="ลบ"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Partner Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b flex items-center justify-between">
                <h2 className="text-2xl font-bold text-textDark">เลือกผู้สอน</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedInstructor(null);
                    setDescription("");
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="ค้นหาผู้สอน..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredInstructors.map((instructor) => (
                    <button
                      key={instructor.id}
                      onClick={() => {
                        setSelectedInstructor(instructor);
                        setDescription(instructor.bio || "");
                      }}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        selectedInstructor?.id === instructor.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        {instructor.profileImage ? (
                          <img
                            src={instructor.profileImage}
                            alt={(instructor.displayName || instructor.name || "Instructor")}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center text-xl font-bold text-white">
                            {(instructor.displayName || instructor.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-textDark">{instructor.displayName || instructor.name || "ไม่ระบุชื่อ"}</h3>
                          <p className="text-xs text-textLight">{instructor.email}</p>
                        </div>
                        {selectedInstructor?.id === instructor.id && (
                          <Check className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span>📚 {instructor.coursesCount || 0} คอร์ส</span>
                        <span>👥 {instructor.studentsCount || 0} คน</span>
                      </div>
                    </button>
                  ))}
                </div>

                {filteredInstructors.length === 0 && (
                  <div className="text-center py-12 text-textLight">
                    <p>ไม่พบผู้สอน</p>
                  </div>
                )}
              </div>

              {selectedInstructor && (
                <div className="p-6 border-t bg-gray-50">
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    คำอธิบาย (แก้ไขได้)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="เช่น สอนคอร์สออนไลน์ด้านการตลาด"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none mb-4"
                    rows={3}
                  />
                  <button
                    onClick={handleAddPartner}
                    className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                  >
                    เพิ่มพาร์ทเนอร์
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
